import { z } from 'zod';

const WEIGHTS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];

/**
 * The verification digit (DV) of a Colombian NIT base, as the DIAN computes it: the base's
 * digits from right to left times 3, 7, 13, 17, 19, …, summed, modulo 11; a remainder of 0 or 1
 * is the digit itself, otherwise 11 minus the remainder.
 *
 * @param {string} base -- The NIT without its digit, digits only.
 * @returns The verification digit.
 */
export const nitCheckDigit = (base: string): number => {
  const sum = [...base]
    .reverse()
    .reduce((total, digit, index) => total + Number(digit) * (WEIGHTS[index] ?? 0), 0);
  const remainder = sum % 11;
  return remainder <= 1 ? remainder : 11 - remainder;
};

/**
 * Reads a NIT as people write it (dots and spaces allowed, a dash before the digit) and checks
 * it: 6 to 10 digits, a dash and the verification digit that matches.
 *
 * @param {string} value -- The NIT as typed.
 * @returns The canonical `NNNNNNNNN-D`, or the Spanish reason it is not valid.
 */
export const checkNit = (value: string): { nit: string } | { error: string } => {
  const compact = value.replace(/[.\s]/g, '');
  const match = /^(\d{6,10})-(\d)$/.exec(compact);
  if (!match) {
    return {
      error: 'Escriba el NIT con su dígito de verificación, por ejemplo 900123456-8',
    };
  }
  const [, base = '', digit = ''] = match;
  const expected = nitCheckDigit(base);
  if (Number(digit) !== expected) {
    return {
      error: `El dígito de verificación no corresponde: para ${base} es ${expected}`,
    };
  }
  return { nit: `${base}-${digit}` };
};

/**
 * A Colombian NIT with its verification digit, stored as `NNNNNNNNN-D`.
 */
export const nitSchema = z
  .string()
  .trim()
  .transform((value, context) => {
    const checked = checkNit(value);
    if ('error' in checked) {
      context.addIssue({ code: 'custom', message: checked.error });
      return z.NEVER;
    }
    return checked.nit;
  });
